import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { PensionAccountType } from '../types/on-boarding.type';
import {
  useCreatePensionAccount,
  useGetPensionList
} from 'common/queries-and-mutations/pension';
import { Loader } from 'components/Loader';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { findNextPage, findPreviousPage } from 'utils';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function PensionAccountInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreatePensionAccount();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isPensionSuccess,
    isLoading: isPensionLoading
  } = useGetPensionList();

  const pension = useMemo(() => {
    if (isPensionSuccess && data.length > 0) {
      return {
        pensionId: data[0].id,
        pensionAdministratorName: data[0].pensionAdministratorName,
        contributorName: data[0].contributorName,
        rsaAddress: data[0].rsaAddress,
        pinNumber: data[0].pinNumber
      };
    } else {
      return {
        pensionId: null,
        pensionAdministratorName: '',
        contributorName: '',
        rsaAddress: '',
        pinNumber: ''
      };
    }
  }, [data, isPensionSuccess]);

  const formik = useFormik({
    initialValues: { ...pension },
    validationSchema: Yup.object().shape({
      pensionAdministratorName: Yup.string().required(
        'Pension Fund Administrator is required'
      ),
      contributorName: Yup.string()
        .required('Contributor name is required')
        .nullable(),
      // pinNumber: Yup.string().required('PIN number is required').nullable(),
      rsaAddress: Yup.string().required('RSA Address is required').nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onPensionSubmit(values);
    }
  });

  const onPensionSubmit = (formValue: PensionAccountType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.PENSION_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, navigate, planAllowPage, dispatch]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.PENSION_INFORMATION));
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.PENSION_INFORMATION}
        timelineStep={2}
      />
      {isPensionLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Financial Asset - Pension Account (RSA)
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>

          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            <div className="my-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="pensionAdministratorName"
                    placeholder=""
                    value={formik.values.pensionAdministratorName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Name of Pension Fund Administrator"
                    error={
                      formik.touched.pensionAdministratorName
                        ? formik.errors.pensionAdministratorName
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="contributorName"
                    placeholder=""
                    value={formik.values.contributorName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Name of Contributor"
                    error={
                      formik.touched.contributorName
                        ? formik.errors.contributorName
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="rsaAddress"
                    placeholder=""
                    value={formik.values.rsaAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Address of RSA"
                    error={
                      formik.touched.rsaAddress ? formik.errors.rsaAddress : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="pinNumber"
                    placeholder=""
                    value={formik.values.pinNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="PIN no."
                    error={
                      formik.touched.pinNumber ? formik.errors.pinNumber : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
